import * as S from "sanctuary";

export const SETTINGS_LEVEL_ROOT = "ROOT";
export const SETTINGS_LEVEL_PROFILE = "PROFILE";

const INITIAL_STATE = {
  settings: {
    [SETTINGS_LEVEL_ROOT]: {},
    [SETTINGS_LEVEL_PROFILE]: {},
  },
  settingsErrors: [],
  meta: S.Nothing,
  metaError: S.Nothing,
};

export const SET_CLIENT_SETTINGS = "global/SET_CLIENT_SETTINGS";
export const setClientSettings = ({ value }, settingsLevel) => ({
  type: SET_CLIENT_SETTINGS,
  payload: {
    value,
    settingsLevel,
  },
});

export const SET_CLIENT_SETTINGS_ERROR = "global/SET_CLIENT_SETTINGS_ERROR";
export const setClientSettingsError = (err, settingsLevel) => ({
  type: SET_CLIENT_SETTINGS_ERROR,
  payload: {
    err,
    settingsLevel,
  },
  error: true,
});

export const SET_CLIENT_META = "global/SET_CLIENT_META";
export const setClientMeta = ({ value }) => ({
  type: SET_CLIENT_META,
  payload: value,
});

export const SET_CLIENT_META_ERROR = "global/SET_CLIENT_META_ERROR";
export const setClientMetaError = (err) => ({
  type: SET_CLIENT_META_ERROR,
  payload: err,
  error: true,
});

export const CLEAR_CLIENT_SETTINGS = "global/CLEAR_CLIENT_SETTINGS";
export const clearClientSettings = () => ({
  type: CLEAR_CLIENT_SETTINGS,
});

const getState = (state) => state.clientConfig;

export const getClientSettings = (state) => {
  const rootSettings = getState(state).settings[SETTINGS_LEVEL_ROOT];
  const profileSettings = getState(state).settings[SETTINGS_LEVEL_PROFILE];
  return {
    ...rootSettings,
    ...profileSettings,
  };
};

export const getClientMeta = (state) => getState(state).meta;

export const getCustomerManagementEnabled = (state) =>
  getClientSettings(state)?.customerManagementEnabled?.value;

export const getPointOfSaleEnabled = (state) =>
  getClientSettings(state)?.pointOfSale?.value?.enabled;

export const getObligation = (state) => (obligationSlug) => {
  const obligationArray = getClientSettings(state)?.obligations;
  if (Array.isArray(obligationArray)) {
    return obligationArray.find(
      (obligation) => obligation.obligationSlug.value === obligationSlug
    );
  } else {
    return null;
  }
};

export const getDescriptionKeys = (state) => (obligationSlug) =>
  getObligation(state)(obligationSlug)?.paymentAttributes?.value?.description
    ?.value ?? null;

export const getSubdescriptionKeys = (state) => (obligationSlug) =>
  getObligation(state)(obligationSlug)?.paymentAttributes?.value?.subDescription
    ?.value ?? null;

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_CLIENT_SETTINGS:
      return INITIAL_STATE;
    case SET_CLIENT_SETTINGS: {
      // Merges API responses that have been deserialized into Sanctuary format.
      const mergeSettings = (a) => (b) => Object.assign({}, a, b);
      const settingsLevel = payload?.settingsLevel ?? SETTINGS_LEVEL_ROOT;
      const currentSettings = state.settings[settingsLevel];
      const newSettings = S.reduce(mergeSettings)(currentSettings)(
        S.Just(payload.value)
      );
      return {
        ...state,
        settings: {
          ...state.settings,
          [settingsLevel]: newSettings,
        },
      };
    }
    case SET_CLIENT_SETTINGS_ERROR: {
      const settingsLevel = payload?.settingsLevel ?? SETTINGS_LEVEL_ROOT;
      const newSettings = {
        ...state.settings,
        [settingsLevel]: INITIAL_STATE.settings[settingsLevel],
      };
      return {
        ...state,
        /*
          Because populating `settings` requires multiple API calls, when an error occurs,
          we clear the section of state (profile or root) that has errored and preserve any other settings sections.

          This behavior change exists to support Cashiering-only clients who only have root level settings files when properly configured on FCS.
          */
        settings: newSettings,
        settingsErrors: state.settingsErrors.concat(S.Just(payload.err)),
      };
    }
    case SET_CLIENT_META:
      return {
        ...state,
        meta: S.Just(payload),
      };
    case SET_CLIENT_META_ERROR:
      return {
        ...state,
        metaError: S.Just(payload),
      };
    default:
      return state;
  }
};

export default reducer;
